import React from 'react';
import type { ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from './context/AuthContext';
import { Permission, getDefaultHomePath } from './types/auth';
import { ProtectedByPermission } from './components/auth/RoleProtection';
import { FEATURE_FLAGS } from './constants/featureFlags';

// Componentes
import LoginPage from './pages/login/LoginPage';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import Dashboard from './pages/dashboard/Dashboard';
import Accounts from './pages/Accounts/Accounts';
import UserManagement from './pages/admin/UserManagement';
import SpecialistsManagement from './pages/admin/SpecialistsManagement';
import SurveyTemplatesManagement from './pages/admin/SurveyTemplatesManagement';
import Layout from './components/layout/Layout';
import ClientsPage from './pages/clients/ClientsPage';
import ReferralsPage from './pages/Referrals/ReferralsPage';
import ClaimsPage from './pages/Claims/ClaimsPage';
import PhoneAgentDashboard from './pages/phone/PhoneAgentDashboard';
import WhatsAppInboxPage from './pages/whatsapp/WhatsAppInboxPage';
import SettingsPage from './pages/settings/SettingsPage';
import PrivacyNoticePage from './pages/legal/PrivacyNoticePage';
// Componente de carga
const LoadingScreen: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      gap: 2
    }}
  >
    <CircularProgress size={60} />
    <Typography variant="h6" color="text.secondary">
      Cargando...
    </Typography>
  </Box>
);

// Componente temporal para páginas no implementadas
const ComingSoon: React.FC<{ title: string }> = ({ title }) => (
  <Box sx={{ p: 3 }}>
    <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}>
      {title}
    </Typography>
    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
      🚧 Esta sección se implementará próximamente.
    </Typography>
  </Box>
);

// Rutas protegidas básicas
interface ProtectedRouteProps { children: ReactNode; }
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isLoggedIn, checkingAuth } = useAuth();
  if (checkingAuth) return <LoadingScreen />;
  return isLoggedIn ? <>{children}</> : <Navigate to="/login" replace />;
};

// Rutas públicas
interface PublicRouteProps { children: ReactNode; }
const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { isLoggedIn, checkingAuth, user } = useAuth();
  if (checkingAuth) return <LoadingScreen />;
  if (!isLoggedIn) return <>{children}</>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={getDefaultHomePath(user)} replace />;
};

const DefaultHomeRedirect: React.FC = () => {
  const { user, isLoggedIn, checkingAuth } = useAuth();
  if (checkingAuth) return <LoadingScreen />;
  if (!isLoggedIn || !user) return <Navigate to="/login" replace />;
  return <Navigate to={getDefaultHomePath(user)} replace />;
};

const AppRoutes: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* ✅ RUTAS PÚBLICAS DE AUTENTICACIÓN */}
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } 
        />
        
        <Route 
          path="/forgot-password" 
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          } 
        />
        
        <Route 
          path="/reset-password/:token" 
          element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          } 
        />
        
        <Route path="/aviso-de-privacidad" element={<PrivacyNoticePage />} />

        {/* ✅ RUTAS PROTEGIDAS */}
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DefaultHomeRedirect />} />
          
          <Route 
            path="dashboard" 
            element={
              <ProtectedByPermission requiredPermission={Permission.VIEW_DASHBOARD}>
                <Dashboard />
              </ProtectedByPermission>
            } 
          />
          
          {/* ✅ Dashboard Telefónico */}
          <Route 
            path="phone-dashboard" 
            element={
              <ProtectedByPermission requiredPermission={Permission.VIEW_PHONE_DASHBOARD}>
                <PhoneAgentDashboard />
              </ProtectedByPermission>
            } 
          />

          <Route
            path="whatsapp"
            element={
              FEATURE_FLAGS.WHATSAPP_MODULE ? (
                <ProtectedByPermission requiredPermission={Permission.VIEW_WHATSAPP_INBOX}>
                  <WhatsAppInboxPage />
                </ProtectedByPermission>
              ) : (
                <DefaultHomeRedirect />
              )
            }
          />

          <Route 
            path="accounts" 
            element={
              <ProtectedByPermission requiredPermission={Permission.VIEW_ACCOUNTS}>
                <Accounts />
              </ProtectedByPermission>
            } 
          />
          
          <Route 
            path="clients" 
            element={
              <ProtectedByPermission requiredPermission={Permission.VIEW_CLIENTS}>
                <ClientsPage />
              </ProtectedByPermission>
            } 
          />
          
          <Route 
            path="clients/:id" 
            element={
              <ProtectedByPermission requiredPermission={Permission.VIEW_CLIENTS}>
                <ComingSoon title="Detalle de Cliente" />
              </ProtectedByPermission>
            } 
          />
          
          <Route 
            path="referrals" 
            element={
              <ProtectedByPermission requiredPermission={Permission.VIEW_REFERRALS}>
                 <ReferralsPage /> 
              </ProtectedByPermission>
            } 
          />
          
          <Route 
            path="referrals/:id" 
            element={
              <ProtectedByPermission requiredPermission={Permission.VIEW_REFERRALS}>
                <ComingSoon title="Detalle de Caso" />
              </ProtectedByPermission>
            } 
          />
          
          <Route 
            path="claims" 
            element={
              <ProtectedByPermission requiredPermission={Permission.VIEW_CLAIMS}>
                <ClaimsPage/>
              </ProtectedByPermission>
            } 
          />
          
          <Route 
            path="claims/:id" 
            element={
              <ProtectedByPermission requiredPermission={Permission.VIEW_CLAIMS}>
                <ComingSoon title="Detalle de Cita" />
              </ProtectedByPermission>
            } 
          />
          
          <Route 
            path="new-entry" 
            element={
              <ProtectedByPermission requiredPermission={Permission.CREATE_ENTRY}>
                <ComingSoon title="Nueva Captura" />
              </ProtectedByPermission>
            } 
          />
          
          <Route 
            path="settings" 
            element={
              <ProtectedByPermission requiredPermission={Permission.ACCESS_SETTINGS}>
                <SettingsPage />
              </ProtectedByPermission>
            } 
          />
          
          {/* ✅ NUEVA RUTA: Gestión de Usuarios */}
          <Route 
            path="users" 
            element={
              <ProtectedByPermission requiredPermission={Permission.MANAGE_USERS}>
                <UserManagement />
              </ProtectedByPermission>
            } 
          />
          
          {/* ✅ NUEVA RUTA: Gestión de Especialistas */}
          <Route 
            path="specialists" 
            element={
              <ProtectedByPermission requiredPermission={Permission.MANAGE_SPECIALISTS}>
                <SpecialistsManagement />
              </ProtectedByPermission>
            } 
          />
          <Route
            path="survey-templates"
            element={
              <ProtectedByPermission requiredPermission={Permission.MANAGE_SURVEY_TEMPLATES}>
                <SurveyTemplatesManagement />
              </ProtectedByPermission>
            }
          />
        </Route>
        
        <Route path="*" element={<DefaultHomeRedirect />} />
      </Routes>
    </Router>
  );
};

export default AppRoutes;